import { api } from "../api";
import type { LicenseRecord } from "../types";
import { fmtTs } from "../utils";
import { LiveCountdown } from "./LiveCountdown";

export function LicensesCard({
  customerId,
  licenses,
}: {
  customerId: string;
  licenses: LicenseRecord[];
}) {
  if (licenses.length === 0) {
    return <div className="card px-5 py-8 text-center text-sm text-ink-500">还没有签发过 license</div>;
  }
  return (
    <div className="card overflow-visible">
      <table className="min-w-full text-sm">
        <thead className="bg-ink-50 text-xs uppercase tracking-wide text-ink-500">
          <tr>
            <th className="px-4 py-2 text-left">serial</th>
            <th className="px-4 py-2 text-left">生效</th>
            <th className="px-4 py-2 text-left">失效</th>
            <th className="px-4 py-2 text-right">频道上限</th>
            <th className="px-4 py-2 text-left">状态</th>
            <th className="px-4 py-2 text-left">文件</th>
          </tr>
        </thead>
        <tbody>
          {[...licenses].reverse().map((lic) => {
            const filename = lic.file?.split("/").pop();
            return (
              <tr key={lic.serial} className="border-t border-ink-100">
                <td className="px-4 py-2 font-mono text-xs">{lic.serial}</td>
                <td className="px-4 py-2 text-xs">{fmtTs(lic.not_before)}</td>
                <td className="px-4 py-2 text-xs">{fmtTs(lic.not_after)}</td>
                <td className="px-4 py-2 text-right">{lic.max_templates}</td>
                <td className="px-4 py-2">
                  <LiveCountdown notAfter={lic.not_after} />
                </td>
                <td className="px-4 py-2 text-xs">
                  {filename ? (
                    <button
                      type="button"
                      className="text-indigo-600 hover:underline"
                      onClick={() => api.downloadLicense(customerId, filename).catch(console.error)}
                    >
                      下载
                    </button>
                  ) : (
                    "-"
                  )}
                  {lic.file && <LicenseFileTooltip lic={lic} />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * 文件名旁边的悬浮提示：鼠标移上去显示这张 license 的解析后字段。
 *
 * 数据直接来自 `LicenseRecord`（后端在签发时就已经写入主台账），不需要再请求接口解码。
 */
function LicenseFileTooltip({ lic }: { lic: LicenseRecord }) {
  return (
    <span className="group relative ml-2 inline-block align-middle">
      <code className="cursor-help font-mono text-ink-500 underline decoration-dotted underline-offset-2 hover:text-indigo-600">
        {lic.file}
      </code>
      <div
        className="invisible absolute right-0 top-full z-30 mt-2 w-[28rem] max-w-[80vw]
          -translate-y-1 rounded-md border border-ink-200 bg-white p-3 text-left text-xs
          opacity-0 shadow-lg ring-1 ring-black/5 transition-all duration-100
          group-hover:visible group-hover:translate-y-0 group-hover:opacity-100"
        role="tooltip"
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="font-semibold text-ink-800">License 内容</span>
          <LiveCountdown notAfter={lic.not_after} />
        </div>
        <dl className="grid grid-cols-[5.5rem_1fr] gap-x-3 gap-y-1.5 leading-relaxed">
          <Row label="serial" mono>
            {lic.serial}
          </Row>
          <Row label="指纹" mono>
            {lic.fingerprint || "-"}
          </Row>
          <Row label="生效">
            {fmtTs(lic.not_before)}
          </Row>
          <Row label="失效">
            {fmtTs(lic.not_after)}
          </Row>
          <Row label="频道上限">{lic.max_templates}</Row>
          <Row label="签发时间">
            {lic.issued_at ? fmtTs(lic.issued_at) : "-"}
          </Row>
          <Row label="签发人">{lic.issuer || "-"}</Row>
          <Row label="用户备注">
            <span className="whitespace-pre-wrap">{lic.user_note || "-"}</span>
          </Row>
          <Row label="文件" mono>
            {lic.file}
          </Row>
        </dl>
      </div>
    </span>
  );
}

function Row({
  label,
  children,
  mono = false,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <>
      <dt className="text-ink-500">{label}</dt>
      <dd className={mono ? "break-all font-mono text-ink-700" : "break-words text-ink-700"}>
        {children}
      </dd>
    </>
  );
}
